// Port of vanilla buildBlockFilename / buildStsBlockFilename / buildFilePrefix /
// buildAssetFolderName / sanitizeForFilename (legacy/index.html lines 2728 + 2803-2836).
//
// Pure functions with no React/store coupling — callers pass the context
// (langCode, shortId, asset metadata) explicitly so admin-side parsing stays
// 1:1 with vanilla output.

export interface FilenameContext {
  /** CONFIG.languageCode (or fallback first 3 chars of project name, uppercased). */
  langCode: string;
  /** S.shortId — short per-session id. */
  shortId: string;
  /** 0-based asset index; emitted as 1-based "Asset{n+1}" in the filename. */
  assetIdx: number;
  /** Asset.title — may be empty; falls back to "Untitled". */
  assetTitle: string;
  /** 1-based version number. */
  version: number;
  /** Optional Voice-Position (1/2/3). Nur gesetzt für Multi-Voice-Assets;
   *  bei Single-Voice-Asset undefined → kein Suffix (#173). Wir lassen
   *  bewusst `undefined` (nicht 0 oder -1) statt einem Sentinel — der
   *  falsy-Check in build*Filename hängt daran. */
  voiceMarker?: number;
}

/**
 * Vanilla parity (legacy/index.html line 2728):
 *   - transliterates German umlauts (ä→ae, ö→oe, ü→ue, Ä→Ae, Ö→Oe, Ü→Ue, ß→ss)
 *   - strips characters not in `[a-zA-Z0-9 _\-+]` (note: SPACE is preserved)
 *   - trims leading/trailing whitespace
 *   - falls back to 'Untitled' when the result is empty
 *
 * Does NOT replace spaces with underscores and does NOT collapse underscores —
 * vanilla output matches admin-side parsing, so any divergence here would
 * break upload paths.
 */
export function sanitizeForFilename(input: string): string {
  return (
    (input ?? '')
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
      .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-zA-Z0-9 _\-+]/g, '')
      .trim() || 'Untitled'
  );
}

/**
 * Sprachkürzel am Anfang jedes Dateinamens: `languageCode`, sonst die ersten
 * drei Zeichen des Projektnamens (Vanilla-Parität), sonst `TRO`.
 *
 * Seit dem Audit 2026-09-24 (C1) nimmt der Dienst nur noch Dateinamen aus
 * `[A-Za-z0-9 _+().-]` an (`server/paths.ts`). Deshalb werden hier genau die
 * Zeichen bereinigt, die bisher durchgerutscht wären (Umlaute
 * transliteriert, Rest entfernt) — für jeden Namen, der schon gültig war,
 * bleibt das Kürzel byte-identisch, damit laufende Projekte ihre
 * Dateinamen behalten.
 */
export function fileLangCode(config: { languageCode?: string | null; project?: string | null } | null | undefined): string {
  const clean = (raw: string): string =>
    raw
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
      .replace(/Ä/g, 'AE').replace(/Ö/g, 'OE').replace(/Ü/g, 'UE').replace(/ß/g, 'SS')
      .replace(/[^A-Za-z0-9 _+().-]/g, '')
      // Nur vorn kürzen: ein Leerzeichen am Ende („DA “ aus „Da Vinci“) war
      // schon immer Teil des Namens und bleibt es.
      .replace(/^[.\s]+/, '');
  return (
    clean(config?.languageCode || '') ||
    clean(config?.project?.slice(0, 3).toUpperCase() || '') ||
    'TRO'
  );
}

export function buildBlockFilename(
  ctx: FilenameContext & { blockIdx: number },
): string {
  const assetName = sanitizeForFilename(ctx.assetTitle || 'Untitled');
  const suffix = ctx.voiceMarker ? `_Voice${ctx.voiceMarker}` : '';
  return `${ctx.langCode}_${ctx.shortId}_Asset${ctx.assetIdx + 1}_${assetName}_v${ctx.version}_BLOCK${ctx.blockIdx + 1}${suffix}.mp3`;
}

/**
 * Context for STS-block filenames. `session` + `takeInSession` are the
 * recording-booth's STSTake metadata (introduced in commit e17e563); both are
 * required since every recorded take carries them and the tool is still
 * pre-release — no legacy data needs accommodating.
 */
export interface StsBlockFilenameContext extends FilenameContext {
  session: number;
  takeInSession: number;
}

/**
 * STS-specific filename builder. Emits
 *   `${prefix}_TAKE{session}-{takeInSession}.mp3`
 * matching the in-app `Take {session}-{n}` labels.
 */
export function buildStsBlockFilename(ctx: StsBlockFilenameContext): string {
  const assetName = sanitizeForFilename(ctx.assetTitle || 'Untitled');
  const prefix = `${ctx.langCode}_${ctx.shortId}_Asset${ctx.assetIdx + 1}_${assetName}_v${ctx.version}`;
  const suffix = ctx.voiceMarker ? `_Voice${ctx.voiceMarker}` : '';
  return `${prefix}_TAKE${ctx.session}-${ctx.takeInSession}${suffix}.mp3`;
}

export function buildFilePrefix(ctx: FilenameContext): string {
  const assetName = sanitizeForFilename(ctx.assetTitle || 'Untitled');
  return `${ctx.langCode}_${ctx.shortId}_Asset${ctx.assetIdx + 1}_${assetName}_v${ctx.version}`;
}

export function buildAssetFolderName(opts: {
  assetIdx: number;
  title: string;
  isCustom: boolean;
}): string {
  const num = opts.assetIdx + 1;
  const title = sanitizeForFilename(opts.title || 'Untitled');
  const prefix = opts.isCustom ? `${num} - Custom` : `${num}`;
  const name = `${prefix} - ${title}`;
  return name.length > 80 ? name.slice(0, 80).trim() : name;
}
